import { useState, useEffect, useCallback, useMemo } from 'react';
import { useOutletContext, useSearchParams } from 'react-router-dom';
import { sb } from '../../lib/supabase';
import useWorkspaceAccess from '../../hooks/useWorkspaceAccess';
import Loader from '../../components/Loader';
import {
  OOTB_CLASSIFICATION_LABEL,
  ootbPillClass,
  suggestBuildTypeFromOotb,
  nextBuildItemRef,
} from '../../lib/workbench';
import { confirmDisposition, fetchOpenApprovalTasks } from '../../lib/tasks';
import { fetchWinningOptionAssessmentMap, applyOptionAssessments } from '../../lib/solutionOptions';

const DISPOSITION_STATUSES = ['approved', 'baselined'];

export default function Disposition() {
  const { project, programme } = useOutletContext();
  const { canWrite } = useWorkspaceAccess();
  const [searchParams] = useSearchParams();
  const focusReqId = searchParams.get('focusReq');
  const orgId = programme?.organisation_id;
  const programmeId = programme?.id;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [requirements, setRequirements] = useState([]);
  const [domainLinks, setDomainLinks] = useState([]);
  const [domains, setDomains] = useState([]);
  const [buildLinks, setBuildLinks] = useState([]);
  const [buildItems, setBuildItems] = useState([]);
  const [typeByName, setTypeByName] = useState(new Map());
  const [saByDomain, setSaByDomain] = useState(new Map());
  const [activeDomain, setActiveDomain] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [openPicker, setOpenPicker] = useState(null);
  const [pickerExisting, setPickerExisting] = useState('');
  const [pickerTitle, setPickerTitle] = useState('');
  const [pickerType, setPickerType] = useState('Config');
  const [approvalTasks, setApprovalTasks] = useState([]);
  const [toast, setToast] = useState(null);

  const showToast = (msg, type = 'error') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 4000);
  };

  const load = useCallback(async () => {
    if (!project?.id || !orgId) return;
    setLoading(true);
    try {
      const { data: reqs } = await sb.from('requirements')
        .select('id, ref, title, ootb_classification, disposition_status, project_id, status')
        .eq('project_id', project.id)
        .in('status', DISPOSITION_STATUSES)
        .order('ref');

      const assessmentMap = await fetchWinningOptionAssessmentMap(project.id);
      const reqList = applyOptionAssessments(reqs || [], assessmentMap);
      const reqIds = reqList.map((r) => r.id);
      const linksPromise = reqIds.length
        ? sb.from('requirement_build_items').select('*').in('requirement_id', reqIds)
        : Promise.resolve({ data: [] });

      const [
        { data: rsd },
        { data: domainRows },
        { data: links },
        { data: items },
        { data: types },
        { data: saRows },
        taskRows,
      ] = await Promise.all([
        sb.from('requirement_solution_domains')
          .select('requirement_id, solution_domain_id, ai_proposed')
          .in('requirement_id', reqIds.length ? reqIds : ['00000000-0000-0000-0000-000000000000']),
        sb.from('solution_domains').select('id, name').order('name'),
        linksPromise,
        sb.from('deliverables')
          .select('id, ref, title, type_id, project_id, kind, deliverable_types(name)')
          .eq('project_id', project.id)
          .eq('kind', 'build_item')
          .order('order_index'),
        sb.from('deliverable_types')
          .select('id, name, order_index')
          .eq('organisation_id', orgId)
          .order('order_index'),
        programmeId
          ? sb.from('v_effective_sa_by_domain').select('*').eq('programme_id', programmeId)
          : Promise.resolve({ data: [] }),
        fetchOpenApprovalTasks(project.id),
      ]);

      const reqIdSet = new Set(reqIds);
      const typeMap = new Map((types || []).map((t) => [t.name, t]));

      setRequirements(reqList);
      setDomainLinks((rsd || []).filter((l) => reqIdSet.has(l.requirement_id)));
      setDomains(domainRows || []);
      setBuildLinks(links || []);
      setBuildItems(items || []);
      setTypeByName(typeMap);
      setSaByDomain(new Map((saRows || []).map((r) => [r.solution_domain_id, r.sa_name])));
      setApprovalTasks((taskRows || []).filter((t) => t.source_artefact_type === 'disposition'));
    } catch (e) {
      console.error('[Disposition] load failed', e);
      showToast('Failed to load disposition data');
    } finally {
      setLoading(false);
    }
  }, [project?.id, orgId, programmeId]);

  useEffect(() => { load(); }, [load]);

  const buildItemById = useMemo(
    () => new Map(buildItems.map((b) => [b.id, b])),
    [buildItems]
  );

  const primaryDomainByReq = useMemo(() => {
    const map = new Map();
    for (const link of domainLinks) {
      if (!map.has(link.requirement_id)) map.set(link.requirement_id, link.solution_domain_id);
    }
    return map;
  }, [domainLinks]);

  const domainPanels = useMemo(() => {
    const counts = new Map();
    for (const req of requirements) {
      const domId = primaryDomainByReq.get(req.id);
      if (!domId) continue;
      if (req.disposition_status !== 'confirmed') {
        counts.set(domId, (counts.get(domId) || 0) + 1);
      }
    }
    const usedDomainIds = new Set([...primaryDomainByReq.values()]);
    return domains
      .filter((d) => usedDomainIds.has(d.id))
      .map((d) => ({
        ...d,
        saName: saByDomain.get(d.id) || 'Unassigned',
        pendingCount: counts.get(d.id) || 0,
      }));
  }, [requirements, primaryDomainByReq, domains, saByDomain]);

  const linksByReq = useMemo(() => {
    const map = new Map();
    for (const link of buildLinks) {
      if (!map.has(link.requirement_id)) map.set(link.requirement_id, []);
      map.get(link.requirement_id).push(link);
    }
    return map;
  }, [buildLinks]);

  const visibleReqs = useMemo(() => requirements.filter((r) => {
    const domId = primaryDomainByReq.get(r.id);
    if (activeDomain && domId !== activeDomain) return false;
    const status = r.disposition_status || 'pending';
    if (statusFilter === 'pending' && status !== 'pending') return false;
    if (statusFilter === 'confirmed' && status !== 'confirmed') return false;
    return true;
  }), [requirements, primaryDomainByReq, activeDomain, statusFilter]);

  const stats = useMemo(() => ({
    total: requirements.length,
    pending: requirements.filter((r) => (r.disposition_status || 'pending') === 'pending').length,
    confirmed: requirements.filter((r) => r.disposition_status === 'confirmed').length,
  }), [requirements]);

  const typeNames = useMemo(() => [...typeByName.keys()], [typeByName]);

  const dispositionTaskByReq = useMemo(() => {
    const map = new Map();
    for (const t of approvalTasks) {
      map.set(t.source_artefact_id, t);
    }
    return map;
  }, [approvalTasks]);

  async function createBuildItem(title, typeName) {
    const typeRow = typeByName.get(typeName);
    const refs = buildItems.map((b) => b.ref);
    const ref = nextBuildItemRef(refs);
    const { data, error } = await sb.from('deliverables').insert({
      project_id: project.id,
      title,
      ref,
      type_id: typeRow?.id || null,
      kind: 'build_item',
      lane: 'not_started',
      order_index: buildItems.length + 1,
    }).select('id, ref, title, type_id, kind, deliverable_types(name)').single();
    if (error) throw error;
    setBuildItems((prev) => [...prev, data]);
    return data;
  }

  async function addLink(requirementId, deliverableId, aiProposed = false) {
    const { data, error } = await sb.from('requirement_build_items').insert({
      requirement_id: requirementId,
      deliverable_id: deliverableId,
      ai_proposed: aiProposed,
    }).select().single();
    if (error) throw error;
    setBuildLinks((prev) => [...prev, data]);
  }

  async function handleAddLink(req) {
    setSaving(true);
    try {
      let deliverableId = pickerExisting;
      if (!deliverableId) {
        const title = pickerTitle.trim();
        if (!title) {
          showToast('Enter a title or pick an existing build item');
          return;
        }
        const created = await createBuildItem(title, pickerType);
        deliverableId = created.id;
      }
      const existing = linksByReq.get(req.id) || [];
      if (existing.some((l) => l.deliverable_id === deliverableId)) {
        showToast('Already linked');
        return;
      }
      await addLink(req.id, deliverableId, false);
      setOpenPicker(null);
      setPickerExisting('');
      setPickerTitle('');
      showToast('Build item linked', 'success');
    } catch (e) {
      console.error('[Disposition] add link failed', e);
      showToast(e.message || 'Failed to add link');
    } finally {
      setSaving(false);
    }
  }

  async function removeLink(linkId, requirementId, deliverableId) {
    setSaving(true);
    try {
      const { error } = await sb.from('requirement_build_items').delete().eq('id', linkId);
      if (error) throw error;
      setBuildLinks((prev) => prev.filter((l) => l.id !== linkId));
      const stillLinked = buildLinks.some(
        (l) => l.id !== linkId && l.deliverable_id === deliverableId
      );
      if (!stillLinked) {
        await sb.from('deliverables').delete().eq('id', deliverableId);
        setBuildItems((prev) => prev.filter((b) => b.id !== deliverableId));
      }
    } catch (e) {
      console.error('[Disposition] remove link failed', e);
      showToast('Failed to remove link');
    } finally {
      setSaving(false);
    }
  }

  async function handleConfirmDisposition(req) {
    const links = linksByReq.get(req.id) || [];
    if (!links.length) {
      showToast('Link at least one build item before confirming');
      return;
    }
    setSaving(true);
    try {
      await confirmDisposition(req.id);
      showToast('Disposition confirmed', 'success');
      await load();
    } catch (e) {
      console.error('[Disposition] confirm failed', e);
      showToast(e.message || 'Failed to confirm disposition');
    } finally {
      setSaving(false);
    }
  }

  async function proposeFromOotb(req) {
    const suggested = suggestBuildTypeFromOotb(req.ootb_classification);
    setSaving(true);
    try {
      const created = await createBuildItem(
        `${req.title.slice(0, 80)} (${suggested})`,
        suggested
      );
      await addLink(req.id, created.id, true);
      showToast('Rex proposal added — review and confirm', 'success');
    } catch (e) {
      console.error('[Disposition] propose failed', e);
      showToast('Failed to create proposal');
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <Loader text="Loading disposition…" />;

  return (
    <div className="wb-disposition-shell">
      <div className="wb-review-head">
        <div>
          <div className="wb-review-title">
            Disposition
            <span className="wb-rex-chip">
              <span className="wb-rex-avatar">R</span>
              Rex proposes
            </span>
          </div>
          <div className="wb-review-sub">
            <b>{programme?.name}</b>
            {' · '}
            {project?.name}
          </div>
        </div>
        <div className="wb-run-progress">
          <div className="wb-run-stat">
            <div className="wb-run-stat-num">{stats.total}</div>
            <div className="wb-run-stat-lbl">Requirements</div>
          </div>
          <div className="wb-run-stat">
            <div className="wb-run-stat-num pending">{stats.pending}</div>
            <div className="wb-run-stat-lbl">Pending</div>
          </div>
          <div className="wb-run-stat">
            <div className="wb-run-stat-num confirmed">{stats.confirmed}</div>
            <div className="wb-run-stat-lbl">Confirmed</div>
          </div>
        </div>
      </div>

      <div className="wb-body-wrap">
        <div className="wb-tree-panel">
          <div className="wb-tree-panel-hdr">
            <div className="wb-tree-panel-title">Solution domains</div>
          </div>
          <div className="wb-tree-body">
            {domainPanels.map((d) => (
              <button
                key={d.id}
                type="button"
                className={`wb-domain-card ${activeDomain === d.id ? 'selected' : ''}`}
                onClick={() => setActiveDomain(activeDomain === d.id ? null : d.id)}
              >
                <div className="wb-domain-name">{d.name}</div>
                <div className="wb-domain-sa">SA: {d.saName}</div>
                {d.pendingCount > 0 && (
                  <div className="wb-domain-count">{d.pendingCount} pending</div>
                )}
              </button>
            ))}
            {!domainPanels.length && (
              <div className="wb-empty-hint">No solution domains linked to approved requirements yet.</div>
            )}
          </div>
        </div>

        <div className="wb-list-panel">
          <div className="wb-toolbar">
            <div className="wb-toolbar-group">
              <span className="wb-toolbar-label">Status</span>
              {['all', 'pending', 'confirmed'].map((f) => (
                <button
                  key={f}
                  type="button"
                  className={`wb-filter-pill ${statusFilter === f ? 'on' : ''}`}
                  onClick={() => setStatusFilter(f)}
                >
                  {f === 'all' ? 'All' : f.charAt(0).toUpperCase() + f.slice(1)}
                </button>
              ))}
            </div>
          </div>

          <div className="wb-review-body">
            {!requirements.length && (
              <div className="wb-empty-hint">
                No approved requirements yet. Complete initiation sizing and approval first.
              </div>
            )}
            {visibleReqs.map((req) => {
              const status = req.disposition_status || 'pending';
              const links = linksByReq.get(req.id) || [];
              const hasProposed = links.some((l) => l.ai_proposed);
              const approvalTask = dispositionTaskByReq.get(req.id);
              const isPending = status === 'pending';

              return (
                <div key={req.id} className={`wb-req-card ${status}${focusReqId === req.id ? ' task-focus' : ''}`}>
                  <div className="wb-req-row">
                    <div className="wb-req-top">
                      <span className="wb-req-ref">{req.ref || '—'}</span>
                      {req.ootb_classification && (
                        <span className={`wb-ootb-pill ${ootbPillClass(req.ootb_classification)}`}>
                          {OOTB_CLASSIFICATION_LABEL[req.ootb_classification] || req.ootb_classification}
                        </span>
                      )}
                      <span className={`wb-req-status-pill ${status}`}>
                        {isPending ? 'Pending' : 'Confirmed'}
                      </span>
                    </div>
                    <div className="wb-req-title">{req.title}</div>

                    <div className="wb-link-section">
                      <div className="wb-link-label">Linked build items (N:M)</div>
                      <div className="wb-link-chips">
                        {links.map((link) => {
                          const bi = buildItemById.get(link.deliverable_id);
                          if (!bi) return null;
                          const typeName = bi.deliverable_types?.name || '—';
                          return (
                            <span
                              key={link.id}
                              className={`wb-bi-chip ${link.ai_proposed ? 'proposed' : ''}`}
                            >
                              <span className="wb-bi-chip-type">{typeName}</span>
                              {bi.ref} — {bi.title}
                              {isPending && canWrite && (
                                <button
                                  type="button"
                                  className="wb-bi-chip-remove"
                                  disabled={saving}
                                  onClick={() => removeLink(link.id, req.id, link.deliverable_id)}
                                  aria-label="Remove link"
                                >
                                  ✕
                                </button>
                              )}
                            </span>
                          );
                        })}
                        {isPending && canWrite && (
                          <button
                            type="button"
                            className="wb-add-bi-btn"
                            disabled={saving}
                            onClick={() => {
                              setOpenPicker(openPicker === req.id ? null : req.id);
                              setPickerType(suggestBuildTypeFromOotb(req.ootb_classification));
                            }}
                          >
                            + Link build item
                          </button>
                        )}
                        {isPending && canWrite && !links.length && (
                          <button
                            type="button"
                            className="wb-add-bi-btn"
                            disabled={saving}
                            onClick={() => proposeFromOotb(req)}
                          >
                            Rex propose from OOTB
                          </button>
                        )}
                      </div>

                      {openPicker === req.id && (
                        <div className="wb-bi-picker show">
                          <div className="wb-bi-picker-row">
                            <select
                              value={pickerExisting}
                              onChange={(e) => setPickerExisting(e.target.value)}
                            >
                              <option value="">Choose existing build item…</option>
                              {buildItems.map((b) => (
                                <option key={b.id} value={b.id}>
                                  {b.ref} — {b.title}
                                </option>
                              ))}
                            </select>
                          </div>
                          <div className="wb-picker-or">or create new —</div>
                          <div className="wb-bi-picker-row">
                            <input
                              type="text"
                              placeholder="New build item title"
                              value={pickerTitle}
                              onChange={(e) => setPickerTitle(e.target.value)}
                            />
                            <select
                              style={{ flex: '0 0 130px' }}
                              value={pickerType}
                              onChange={(e) => setPickerType(e.target.value)}
                            >
                              {typeNames.map((t) => (
                                <option key={t} value={t}>{t}</option>
                              ))}
                            </select>
                          </div>
                          <div className="wb-bi-picker-actions">
                            <button type="button" className="wb-act-btn" onClick={() => setOpenPicker(null)}>Cancel</button>
                            <button
                              type="button"
                              className="wb-act-btn confirm"
                              disabled={saving}
                              onClick={() => handleAddLink(req)}
                            >
                              Add link
                            </button>
                          </div>
                        </div>
                      )}
                    </div>

                    {hasProposed && (
                      <div className="wb-rex-attribution">
                        <span className="wb-rex-avatar sm">R</span>
                        Proposed by Rex from OOTB classification — SA confirms
                        {approvalTask?.profiles?.full_name && (
                          <> · Approval task: {approvalTask.profiles.full_name}</>
                        )}
                      </div>
                    )}

                    {isPending && canWrite && (
                      <div className="wb-req-actions">
                        <button
                          type="button"
                          className="wb-act-btn confirm"
                          disabled={saving}
                          onClick={() => handleConfirmDisposition(req)}
                        >
                          Confirm disposition
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
            {requirements.length > 0 && !visibleReqs.length && (
              <div className="wb-empty-hint">No requirements match the current filters.</div>
            )}
          </div>
        </div>
      </div>

      {toast && (
        <div className={`toast ${toast.type === 'success' ? 'toast-success' : 'toast-error'}`}>
          {toast.msg}
        </div>
      )}
    </div>
  );
}
